/**
 * Live session protocol contracts (SI-0-03 / SI-1-01 / SI-1-02 / SI-1-04 / S-D5).
 *
 * Every JSON-RPC method the session host registers is an Operation (or a
 * push-notification schema) here. Host, React client, and MCP tools import
 * the same set — a WS message with no contract is the same smell as a raw
 * `fetch()` (PRD §6.2 / §14 G2).
 *
 * SI-1-02: `session.act` requires a client `idempotencyKey`; the response
 * surfaces `deduped` so at-least-once delivery is observable without a second
 * append. Gapless client assertion lives in `@excellent/ui/session/gapless`.
 *
 * SI-1-04: `session.approve` is the human-in-the-loop write path; S-D11
 * capability (owner always / else mode=approver) is enforced in host +
 * `approvals.ts`, not in the contract schema.
 *
 * URN grammar (SI-0-01): `live_session:<integer_id>`.
 */

import { z } from "zod";
import type { Operation } from "../types";

export const SESSION_KINDS = ["chat", "terminal", "agent-run", "qa-run"] as const;
export type SessionKind = (typeof SESSION_KINDS)[number];

export const SESSION_MODES = ["actor", "observer", "approver"] as const;
export type SessionMode = (typeof SESSION_MODES)[number];

/**
 * Bijective registry with host.ts `SESSION_JSONRPC_METHODS` (G2).
 * Adding a method = adding an entry here AND on the host — the gate enforces both.
 */
export const SESSION_CONTRACT_METHODS = [
  "session.create",
  "session.attach",
  "session.act",
  "session.approve",
  "session.override",
  "session.detach",
  "session.heartbeat",
  // SI-G2 (G2a): read-only surface — requestable over WS JSON-RPC AND served
  // as HTTP read ops (check-session-contract-coverage keeps host ⇄ contract
  // bijective, so each read also has a host dispatcher branch in host.ts).
  "session.list",
  "session.trajectory",
  "session.action",
  "session.presence",
  "session.compacted",
] as const;

export type SessionJsonRpcMethod = (typeof SESSION_CONTRACT_METHODS)[number];

const sessionKind = z.enum(SESSION_KINDS);
const sessionMode = z.enum(SESSION_MODES);

// ── session.create ──────────────────────────────────────────────────────────

const createBody = z.object({
  kind: sessionKind,
  title: z.string().min(1).optional(),
  originTaskId: z.number().int().positive().optional(),
  /** Chat-kind only (SI-2-02): operate|develop — ignored for other kinds. */
  mode: z.enum(["operate", "develop"]).optional(),
  /**
   * Chat-kind only (SI-2-02): rebind an existing excellent_chat_conversations.uid
   * instead of minting a new projection row (history resume).
   */
  conversationUid: z.string().min(1).optional(),
});
export type SessionCreateBody = z.infer<typeof createBody>;

const createResponse = z.object({
  urn: z.string().min(1),
  headSeq: z.number().int().nonnegative(),
  id: z.number().int().positive(),
});
export type SessionCreateResponse = z.infer<typeof createResponse>;

const createOp: Operation<void, void, SessionCreateBody, SessionCreateResponse> & {
  jsonrpcMethod: "session.create";
} = {
  method: "POST",
  path: () => "/api/sessions/create",
  body: createBody,
  response: createResponse,
  jsonrpcMethod: "session.create",
};

// ── session.act ─────────────────────────────────────────────────────────────

const actBody = z.object({
  urn: z.string().min(1),
  intent: z.record(z.string(), z.unknown()),
  idempotencyKey: z.string().min(1),
});
export type SessionActBody = z.infer<typeof actBody>;

const actResponse = z.object({
  seq: z.number().int().positive(),
  /** True when this act was a replay of an earlier key (no second append). */
  deduped: z.boolean(),
});
export type SessionActResponse = z.infer<typeof actResponse>;

const actOp: Operation<void, void, SessionActBody, SessionActResponse> & {
  jsonrpcMethod: "session.act";
} = {
  method: "POST",
  path: () => "/api/sessions/act",
  body: actBody,
  response: actResponse,
  jsonrpcMethod: "session.act",
};

// ── session.attach ──────────────────────────────────────────────────────────

const attachBody = z.object({
  urn: z.string().min(1),
  mode: sessionMode,
  /** Last seq the client has applied. Null/omitted → full snapshot path. */
  afterSeq: z.number().int().nonnegative().nullable().optional(),
});
export type SessionAttachBody = z.infer<typeof attachBody>;

const actionRow = z.object({
  seq: z.number().int().positive(),
  kind: z.string(),
  actorId: z.string(),
  payload: z.unknown(),
  ts: z.string(),
});
export type SessionActionRow = z.infer<typeof actionRow>;

const participant = z.object({
  actorId: z.string(),
  mode: sessionMode,
});
export type SessionParticipant = z.infer<typeof participant>;

const attachResponse = z.object({
  urn: z.string().min(1),
  snapshot: z.unknown().nullable(),
  snapshotSeq: z.number().int().nonnegative(),
  headSeq: z.number().int().nonnegative(),
  /** True when the client's afterSeq was below the snapshot floor (R-S4). */
  compacted: z.boolean(),
  /** Present when compacted — the floor the client must resync from. */
  snapshotFloor: z.number().int().nonnegative().optional(),
  /** Ordered actions after max(afterSeq, snapshotSeq) for HTTP attach. */
  actions: z.array(actionRow),
  participants: z.array(participant),
  capabilities: z.array(z.string()),
});
export type SessionAttachResponse = z.infer<typeof attachResponse>;

const attachOp: Operation<void, void, SessionAttachBody, SessionAttachResponse> & {
  jsonrpcMethod: "session.attach";
} = {
  method: "POST",
  path: () => "/api/sessions/attach",
  body: attachBody,
  response: attachResponse,
  jsonrpcMethod: "session.attach",
};

// ── session.approve ─────────────────────────────────────────────────────────

const approveBody = z.object({
  urn: z.string().min(1),
  actionSeq: z.number().int().positive(),
  verdict: z.enum(["approved", "rejected"]),
  note: z.string().optional(),
});
export type SessionApproveBody = z.infer<typeof approveBody>;

const approveResponse = z.object({
  seq: z.number().int().positive(),
});
export type SessionApproveResponse = z.infer<typeof approveResponse>;

const approveOp: Operation<void, void, SessionApproveBody, SessionApproveResponse> & {
  jsonrpcMethod: "session.approve";
} = {
  method: "POST",
  path: () => "/api/sessions/approve",
  body: approveBody,
  response: approveResponse,
  jsonrpcMethod: "session.approve",
};

// ── session.override (V3 human override capture) ────────────────────────────

const overrideBody = z.object({
  urn: z.string().min(1),
  path: z.enum(["accept", "reject", "edit"]),
  claimSeq: z.number().int().positive().optional(),
  before: z.unknown().optional(),
  after: z.unknown().optional(),
  editedPredicate: z.string().min(1).optional(),
  reason: z
    .union([
      z.string(),
      z.object({
        code: z.string().optional(),
        text: z.string().optional(),
      }),
    ])
    .optional(),
  subject: z.string().optional(),
});
export type SessionOverrideBody = z.infer<typeof overrideBody>;

const overrideResponse = z.union([
  z.object({
    skipped: z.literal(true),
    reason: z.literal("no-delta"),
  }),
  z.object({
    skipped: z.literal(false),
    seq: z.number().int().positive(),
    delta: z.unknown(),
    chainHash: z.string().nullable().optional(),
  }),
]);
export type SessionOverrideResponse = z.infer<typeof overrideResponse>;

const overrideOp: Operation<
  void,
  void,
  SessionOverrideBody,
  SessionOverrideResponse
> & {
  jsonrpcMethod: "session.override";
} = {
  method: "POST",
  path: () => "/api/sessions/override",
  body: overrideBody,
  response: overrideResponse,
  jsonrpcMethod: "session.override",
};

// ── session.detach / session.heartbeat ──────────────────────────────────────

const detachBody = z.object({
  urn: z.string().min(1),
});
export type SessionDetachBody = z.infer<typeof detachBody>;

const detachResponse = z.object({
  ok: z.literal(true),
});
export type SessionDetachResponse = z.infer<typeof detachResponse>;

const detachOp: Operation<void, void, SessionDetachBody, SessionDetachResponse> & {
  jsonrpcMethod: "session.detach";
} = {
  method: "POST",
  path: () => "/api/sessions/detach",
  body: detachBody,
  response: detachResponse,
  jsonrpcMethod: "session.detach",
};

const heartbeatBody = z.object({
  urn: z.string().min(1),
});
export type SessionHeartbeatBody = z.infer<typeof heartbeatBody>;

const heartbeatResponse = z.object({
  ok: z.literal(true),
  ts: z.string(),
});
export type SessionHeartbeatResponse = z.infer<typeof heartbeatResponse>;

const heartbeatOp: Operation<void, void, SessionHeartbeatBody, SessionHeartbeatResponse> & {
  jsonrpcMethod: "session.heartbeat";
} = {
  method: "POST",
  path: () => "/api/sessions/heartbeat",
  body: heartbeatBody,
  response: heartbeatResponse,
  jsonrpcMethod: "session.heartbeat",
};

// ── host → client notifications (no HTTP twin; still contracts) ─────────────

/** Push: one authoritative action (fan-out). */
export const sessionActionNotification = {
  jsonrpcMethod: "session.action" as const,
  params: z.object({
    seq: z.number().int().positive(),
    action: z.object({
      kind: z.string(),
      payload: z.unknown(),
    }),
    actorId: z.string(),
    ts: z.string(),
  }),
};

/** Push: participant join/leave/idle. */
export const sessionPresenceNotification = {
  jsonrpcMethod: "session.presence" as const,
  params: z.object({
    event: z.enum(["joined", "left", "idle"]),
    participant: participant,
  }),
};

/**
 * Push: client afterSeq was below the snapshot floor — resync from snapshot
 * (never silently gapped; R-S4 / P5).
 */
export const sessionCompactedNotification = {
  jsonrpcMethod: "session.compacted" as const,
  params: z.object({
    snapshotFloor: z.number().int().nonnegative(),
  }),
};

// ── claims on review (P2-U2, verification-loop Wave B-UI) ───────────────────
//
// HTTP read twins of the MCP `session_claim` listing + `session_claim_slice`
// (V2-05) — same core readers (`listSessionClaims` / `reviewClaimSlice`), no
// parallel claim store. No `jsonrpcMethod`: these are review reads, not
// session-protocol messages, so the G2 bijective registry is untouched.

const claimRow = z.object({
  seq: z.number().int().positive(),
  actorId: z.string(),
  /** String or structured predicate, exactly as asserted. */
  predicate: z.unknown(),
  fromSeq: z.number().int(),
  toSeq: z.number().int(),
  chainHash: z.string().nullable(),
  createdAt: z.string(),
  /** VP-E1 / SKU A: falsifier(s) from the claim payload when present. */
  falsifiers: z.unknown().nullable().optional(),
});
export type SessionClaimRow = z.infer<typeof claimRow>;

const claimSessionGroup = z.object({
  id: z.number().int().positive(),
  urn: z.string().min(1),
  uid: z.string().nullable(),
  kind: sessionKind,
  title: z.string().nullable(),
  claims: z.array(claimRow),
});
export type SessionClaimGroup = z.infer<typeof claimSessionGroup>;

const claimsQuery = z.object({
  /** Task under review — sessions resolve via `origin_task_id`. */
  task_id: z.coerce.number().int().positive(),
});

const claimsResponse = z.object({
  sessions: z.array(claimSessionGroup),
});
export type SessionClaimsResponse = z.infer<typeof claimsResponse>;

const claimsOp: Operation<
  void,
  z.infer<typeof claimsQuery>,
  void,
  SessionClaimsResponse
> = {
  method: "GET",
  path: () => "/api/sessions/claims",
  query: claimsQuery,
  response: claimsResponse,
};

const claimSliceQuery = z.object({
  urn: z.string().min(1),
  claim_seq: z.coerce.number().int().positive(),
});

const sliceActionRow = z.object({
  seq: z.number().int().positive(),
  kind: z.string(),
  actorId: z.string(),
  payload: z.unknown(),
  chainHash: z.string().nullable(),
  payloadHash: z.string().nullable(),
});
export type SessionSliceActionRow = z.infer<typeof sliceActionRow>;

const sliceHashRow = z.object({
  seq: z.number().int().positive(),
  payloadHash: z.string().nullable(),
  chainHash: z.string().nullable(),
  kind: z.string(),
  actorId: z.string(),
});

const claimSliceResponse = z.union([
  z.object({ ok: z.literal(false), reason: z.string() }),
  z.object({
    ok: z.literal(true),
    urn: z.string(),
    sessionUid: z.string().nullable(),
    claimSeq: z.number().int().positive(),
    predicate: z.unknown(),
    fromSeq: z.number().int(),
    toSeq: z.number().int(),
    claimChainHash: z.string().nullable(),
    binding: z.array(
      z.object({
        sessionUid: z.string().nullable(),
        seq: z.number().int(),
        chainHash: z.string().nullable(),
      }),
    ),
    /** Payload when intact; hash-only when any row is tombstoned (SIM 16). */
    slice: z.union([
      z.object({
        kind: z.literal("payload"),
        payload: z.unknown(),
        fromSeq: z.number().int(),
        toSeq: z.number().int(),
        actions: z.array(sliceActionRow),
      }),
      z.object({
        kind: z.literal("hash-only"),
        hashes: z.array(sliceHashRow),
        fromSeq: z.number().int(),
        toSeq: z.number().int(),
      }),
      z.object({ kind: z.literal("missing"), reason: z.string() }),
    ]),
    citation: z.string(),
  }),
]);
export type SessionClaimSliceResponse = z.infer<typeof claimSliceResponse>;

const claimSliceOp: Operation<
  void,
  z.infer<typeof claimSliceQuery>,
  void,
  SessionClaimSliceResponse
> = {
  method: "GET",
  path: () => "/api/sessions/claim-slice",
  query: claimSliceQuery,
  response: claimSliceResponse,
};

// ── SI-G2 (release plan XS-G10) — read-only session list + trajectory ────────
// Read twins of the session substrate that feed the `/agents/sessions` surface
// (G2a) and its trajectory detail view (G2b). Both are pure SELECTs (READ
// authz). They are ALSO requestable over the WS JSON-RPC channel so the host
// method registry and SESSION_CONTRACT_METHODS stay bijective
// (check-session-contract-coverage, G2) — each read has a host dispatcher
// branch in `excellent/server/sessions/host.ts#handleJsonRpc`.

const sessionListRow = z.object({
  id: z.number().int().positive(),
  urn: z.string().min(1),
  uid: z.string().nullable(),
  kind: sessionKind,
  title: z.string().nullable(),
  originTaskId: z.number().int().nullable(),
  headSeq: z.number().int().nonnegative(),
  snapshotFloor: z.number().int().nonnegative(),
  createdAt: z.string(),
  actionCount: z.number().int().nonnegative(),
  /** WS2 observe: null-origin runs are first-class, not filtered out. */
  hasNullOrigin: z.boolean(),
  /** Derived from the trajectory so operators do not grep SQLite for activity. */
  toolCallCount: z.number().int().nonnegative(),
  /** run.usage actions projected at worker-run finalize time, when present. */
  runUsageCount: z.number().int().nonnegative(),
  participantCount: z.number().int().nonnegative(),
});
export type SessionListRow = z.infer<typeof sessionListRow>;

const sessionListQuery = z.object({
  kind: sessionKind.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  beforeId: z.coerce.number().int().positive().optional(),
});

const sessionListResponse = z.object({
  sessions: z.array(sessionListRow),
});
export type SessionListResponse = z.infer<typeof sessionListResponse>;

const sessionListOp: Operation<
  void,
  z.infer<typeof sessionListQuery>,
  void,
  SessionListResponse
> = {
  method: "GET",
  path: () => "/api/sessions/list",
  query: sessionListQuery,
  response: sessionListResponse,
};

const sessionTrajectoryQuery = z.object({
  urn: z.string().min(1),
  fromSeq: z.coerce.number().int().positive().optional(),
  toSeq: z.coerce.number().int().positive().optional(),
});

export const sessionTrajectoryActionRow = z.object({
  seq: z.number().int().positive(),
  kind: z.string(),
  actorId: z.string(),
  payload: z.unknown(),
  ts: z.string(),
  chainHash: z.string().nullable(),
  /** SI-G2 G2b tombstone marker — true when the payload was compacted away.
   *  A compacted session must never render as "the agent did nothing". */
  tombstoned: z.boolean(),
  payloadHash: z.string().nullable(),
});
export type SessionTrajectoryActionRow = z.infer<typeof sessionTrajectoryActionRow>;

const sessionTrajectoryResponse = z.object({
  urn: z.string().min(1),
  sessionId: z.number().int().positive(),
  kind: sessionKind,
  title: z.string().nullable(),
  originTaskId: z.number().int().nullable(),
  headSeq: z.number().int().nonnegative(),
  snapshotFloor: z.number().int().nonnegative(),
  /** True when the session was compacted (snapshot_floor > 0) — the detail
   *  view must surface the tombstone marker so older work is not mistaken
   *  for "nothing happened". (SI-G2 G2b) */
  compacted: z.boolean(),
  seqRange: z
    .object({ from: z.number().int(), to: z.number().int() })
    .nullable(),
  citation: z.string().nullable(),
  summary: z.string(),
  actions: z.array(sessionTrajectoryActionRow),
});
export type SessionTrajectoryResponse = z.infer<typeof sessionTrajectoryResponse>;

const sessionTrajectoryOp: Operation<
  void,
  z.infer<typeof sessionTrajectoryQuery>,
  void,
  SessionTrajectoryResponse
> = {
  method: "GET",
  path: () => "/api/sessions/trajectory",
  query: sessionTrajectoryQuery,
  response: sessionTrajectoryResponse,
};

// ── WS upgrade path ─────────────────────────────────────────────────────────

/** Empty body for the non-upgrade GET probe on the WS path. */
const emptyResponse = z.object({
  error: z.string(),
});

/**
 * The WebSocket upgrade path. Method is GET (HTTP upgrade rules); authorization
 * is a real capability descriptor registered via `mountWs` — not the READ
 * sentinel — so R-S3 (upgrade bypass) cannot recur.
 */
const wsOp: Operation<void, void, void, { error: string }> = {
  method: "GET",
  path: () => "/api/sessions/ws",
  response: emptyResponse,
};

export const sessions = {
  create: createOp,
  attach: attachOp,
  act: actOp,
  approve: approveOp,
  override: overrideOp,
  detach: detachOp,
  heartbeat: heartbeatOp,
  claims: claimsOp,
  claimSlice: claimSliceOp,
  list: sessionListOp,
  trajectory: sessionTrajectoryOp,
  ws: wsOp,
  // Notification schemas (host → client); not mount()-able HTTP ops.
  action: sessionActionNotification,
  presence: sessionPresenceNotification,
  compacted: sessionCompactedNotification,
};
